import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GlobalShippingSettings, UpdateGlobalShippingSettingsRequest } from '../../../infrastructure/ShippingClient';
import { shippingClient } from '../../../infrastructure/ShippingClient';

export const GLOBAL_SHIPPING_SETTINGS_QUERY_KEYS = {
  all: ['globalShippingSettings'] as const,
  settings: () => [...GLOBAL_SHIPPING_SETTINGS_QUERY_KEYS.all, 'settings'] as const,
};

const DEFAULT_GLOBAL_SETTINGS: GlobalShippingSettings = {
  systemMessage: null,
  globalShippingDelayDay: null,
};

export interface UseGlobalShippingSettingsReturn {
  settings: GlobalShippingSettings;
  isLoading: boolean;
  isUpdating: boolean;
  error: Error | null;
  updateError: Error | null;
  updateSettings: (settings: UpdateGlobalShippingSettingsRequest) => Promise<GlobalShippingSettings>;
  refreshSettings: () => void;
}

export const useGlobalShippingSettings = (): UseGlobalShippingSettingsReturn => {
  const queryClient = useQueryClient();

  const {
    data: settings = DEFAULT_GLOBAL_SETTINGS,
    isLoading,
    error,
    refetch: refreshSettings,
  } = useQuery({
    queryKey: GLOBAL_SHIPPING_SETTINGS_QUERY_KEYS.settings(),
    queryFn: () => shippingClient.getGlobalShippingSettings(),
    staleTime: 5 * 60 * 1000,
  });

  const updateMutation = useMutation({
    mutationFn: (request: UpdateGlobalShippingSettingsRequest) => shippingClient.updateGlobalShippingSettings(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GLOBAL_SHIPPING_SETTINGS_QUERY_KEYS.settings() });
    },
  });

  const updateSettings = async (request: UpdateGlobalShippingSettingsRequest): Promise<GlobalShippingSettings> => {
    return updateMutation.mutateAsync(request);
  };

  return {
    settings,
    isLoading,
    isUpdating: updateMutation.isPending,
    error,
    updateError: updateMutation.error,
    updateSettings,
    refreshSettings,
  };
};

